import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { log, warn, error } from '../utils/logger'
import type {
    User,
    AuthTokens,
    Game,
    Platform,
    PlatformAlias,
    Genre,
    Store,
    Price,
    WishlistItem,
    ApiResponse,
    PaginatedApiResponse,
    SearchFilters,
    FeaturedGamePlatform,
    GamePlatform,
    GamePlatformImage,
    PriceDropGamePlatform,
    GamePlatformProductId,
    GameExternalId,
    PasskeyRegistrationOptions,
    PasskeyAuthenticationOptions,
    PasskeyRegistrationResponse,
    PasskeyAuthenticationResponse,
    PasskeyRegistrationFinishData,
    UserCredential,
    Notification,
    LowestPrice,
    Comment,
    CensoredWord,
    CommentSetting,
    CreateCommentData,
    UpdateCommentData,
    DeleteAllPricesResponse,
    StoreRating,
    StoreRatingsResponse,
    CreateStoreRatingData,
    UpdateStoreRatingData,
    CreateGameRatingData,
    UpdateGameRatingData,
    Sale,
} from '../types'

// Base API configuration
const API_BASE_URL =
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) ||
    process.env.VITE_API_URL ||
    'https://api.spellen.land/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
})

// No refresh token functionality needed for single token system

// Request interceptor to add auth token and country
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const tokens = localStorage.getItem('tokens')
        if (tokens) {
            try {
                const { accessToken } = JSON.parse(tokens) as AuthTokens
                if (accessToken) {
                    config.headers.Authorization = `Bearer ${accessToken}`
                    log('🔑 Added auth token to request:', config.url)
                } else {
                    warn('⚠️ No access token found in stored tokens')
                }
            } catch (parseError) {
                error('❌ Error parsing tokens from localStorage:', parseError)
                localStorage.removeItem('tokens')
            }
        } else {
            log('📭 No tokens in localStorage for request:', config.url)
        }

        // Add country parameter to requests that need it
        const country = localStorage.getItem('country') || 'NL'

        // Only add country parameter to specific endpoints that need it
        const endpointsNeedingCountry = [
            '/games',
            '/games/search',
            '/games/featured',
            '/games/price-drops',
            '/games/biggest-price-drops',
            '/games/recently-released',
            '/games/platform',
            '/wishlists',
            '/notifications',
            '/comments'
        ]

        const needsCountry = endpointsNeedingCountry.some(endpoint => {
            if (!config.url) return false

            // More specific matching to avoid conflicts
            if (endpoint === '/games/platform') {
                // Only match /games/platform/* but not /platforms/*
                return config.url.includes('/games/platform') && !config.url.includes('/platforms/')
            }

            return config.url.includes(endpoint)
        })

        if (needsCountry) {
            if (config.params && typeof config.params === 'object') {
                config.params.country = country
            } else if (config.params === undefined) {
                config.params = { country }
            }
        }

        return config
    },
    (error) => {
        error('💥 Request interceptor error:', error)
        return Promise.reject(error)
    }
)

// Response interceptor for token refresh
api.interceptors.response.use(
    (response) => {
        log('✅ API Response:', response.config.url, response.status)
        return response
    },
    async (error) => {
        const originalRequest = error.config
        log('❌ API Error:', {
            url: originalRequest?.url,
            status: error.response?.status,
            message: error.response?.data?.message
        })

        // For 401 errors, just clear tokens and logout since we don't have refresh tokens
        // But don't trigger logout for login endpoint failures (expected auth failures)
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
            log('🔐 Got 401, clearing tokens and logging out')
            localStorage.removeItem('tokens')
            // Dispatch a custom event to notify the auth context about the logout
            log('📢 Dispatching auth:logout event')
            window.dispatchEvent(new CustomEvent('auth:logout'))
        }

        return Promise.reject(error)
    }
)

// Auth API
export const authApi = {
    register: (data: { email: string; password: string; country: string }): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
        api.post('/auth/register', data),

    login: (data: { email: string; password: string }): Promise<AxiosResponse<ApiResponse<{ user: User; token: string }>>> =>
        api.post('/auth/login', data),

    verifyEmail: (data: { token: string }): Promise<AxiosResponse<ApiResponse<{ user: User; token: string }>>> =>
        api.post('/auth/verify-email', data),

    resendVerification: (data: { email: string }): Promise<AxiosResponse<ApiResponse<null>>> =>
        api.post('/auth/resend-verification', data),

    // No refresh token endpoint since backend uses single token system

    getProfile: (): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
        api.get('/auth/me'),

    forgotPassword: (data: { email: string }): Promise<AxiosResponse<ApiResponse<null>>> =>
        api.post('/auth/forgot-password', data),

    resetPassword: (data: { token: string; password: string }): Promise<AxiosResponse<ApiResponse<null>>> =>
        api.post('/auth/reset-password', data),

    changePassword: (data: { currentPassword: string; newPassword: string }): Promise<AxiosResponse<ApiResponse<null>>> =>
        api.post('/auth/change-password', data),

    // Passkey endpoints
    passkeyRegistrationBegin: (data?: { nickname?: string }): Promise<AxiosResponse<ApiResponse<PasskeyRegistrationOptions>>> =>
        api.post('/auth/passkey/registration/begin', data),

    passkeyRegistrationFinish: (data: PasskeyRegistrationResponse, nickname?: string): Promise<AxiosResponse<ApiResponse<{ verified: boolean }>>> =>
        api.post(`/auth/passkey/registration/finish${nickname ? `?nickname=${encodeURIComponent(nickname)}` : ''}`, data),

    passkeyAuthenticationBegin: (data?: { email?: string }): Promise<AxiosResponse<ApiResponse<PasskeyAuthenticationOptions>>> =>
        api.post('/auth/passkey/authentication/begin', data),

    passkeyAuthenticationFinish: (data: PasskeyAuthenticationResponse, challenge: string): Promise<AxiosResponse<ApiResponse<{ user: User; token: string }>>> =>
        api.post('/auth/passkey/authentication/finish', { ...data, challenge }),

    getPasskeyCredentials: (): Promise<AxiosResponse<ApiResponse<UserCredential[]>>> =>
        api.get('/auth/passkey/credentials'),

    deletePasskeyCredential: (credentialId: string): Promise<AxiosResponse<ApiResponse<null>>> =>
        api.delete(`/auth/passkey/credentials/${credentialId}`),

    // Passwordless registration endpoints
    registerWithPasskeyBegin: (data: { email: string; country?: string }): Promise<AxiosResponse<ApiResponse<PasskeyRegistrationOptions & { tempUserId: string }>>> =>
        api.post('/auth/register-with-passkey/begin', data),

    registerWithPasskeyFinish: (data: PasskeyRegistrationFinishData): Promise<AxiosResponse<ApiResponse<{ user: User; token: string }>>> =>
        api.post('/auth/register-with-passkey/finish', data),
}

// Games API
export const gamesApi = {
    getGames: (params?: SearchFilters & { page?: number; limit?: number }): Promise<AxiosResponse<PaginatedApiResponse<Game>>> =>
        api.get('/games', { params }),

    getFilteredGamePlatforms: (params?: SearchFilters & { page?: number; limit?: number }): Promise<AxiosResponse<PaginatedApiResponse<GamePlatform>>> =>
        api.get('/games/game-platforms', { params }),

    getGame: (slug: string, platformSlug?: string, country?: string): Promise<AxiosResponse<ApiResponse<Game | GamePlatform>>> =>
        platformSlug
            ? api.get(`/games/${slug}/platforms/${platformSlug}`, { params: { country } })
            : api.get(`/games/slug/${slug}`, { params: { country } }),

    getGameDetail: (slug: string, platformSlug: string, country?: string): Promise<AxiosResponse<ApiResponse<GamePlatform>>> =>
        api.get(`/games/${slug}/platforms/${platformSlug}/detail`, { params: { country } }),

    getGameById: (id: string): Promise<AxiosResponse<ApiResponse<Game>>> =>
        api.get(`/games/${id}`),

    getGamePlatforms: (slug: string, country?: string): Promise<AxiosResponse<ApiResponse<GamePlatform[]>>> =>
        api.get(`/games/${slug}/platforms`, { params: { country } }),

    getAdminGamePlatformsWithAllPrices: (gameId: string): Promise<AxiosResponse<ApiResponse<GamePlatform[]>>> =>
        api.get(`/games/admin/${gameId}/platforms`),

    searchGames: (query: string, country?: string): Promise<AxiosResponse<ApiResponse<GamePlatform[]>>> =>
        api.get('/games/search', { params: { q: query, limit: 20, country } }),

    createGame: (data: Partial<Game>): Promise<AxiosResponse<ApiResponse<Game>>> =>
        api.post('/games', data),

    updateGame: (id: string, data: Partial<Game>): Promise<AxiosResponse<ApiResponse<Game>>> =>
        api.put(`/games/${id}`, data),

    deleteGame: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/games/${id}`),

    getFeaturedGames: (country?: string): Promise<AxiosResponse<ApiResponse<FeaturedGamePlatform[]>>> =>
        api.get('/games/featured', { params: { country } }),

    getPriceDrops: (page?: number, limit?: number, filters?: { gameName?: string; platform?: string }, country?: string): Promise<AxiosResponse<PaginatedApiResponse<PriceDropGamePlatform>>> =>
        api.get('/games/price-drops', { params: { page, limit, ...filters, country } }),

    getBiggestPriceDrops: (country?: string): Promise<AxiosResponse<PaginatedApiResponse<PriceDropGamePlatform>>> =>
        api.get('/games/biggest-price-drops', { params: { page: 1, limit: 15, country } }),

    getRecentGames: (): Promise<AxiosResponse<ApiResponse<FeaturedGamePlatform[]>>> =>
        api.get('/games/recent'),

    getRecentlyReleasedGames: (country?: string): Promise<AxiosResponse<ApiResponse<FeaturedGamePlatform[]>>> =>
        api.get('/games/recently-released', { params: { country } }),

    getReleaseDatePage: (params: {
        date: string;
        search?: string;
        platforms?: string;
        genres?: string[];
        priceMin?: number;
        priceMax?: number;
        rating?: string;
        sortOrder?: 'asc' | 'desc';
        limit?: number;
        country?: string;
    }): Promise<AxiosResponse<ApiResponse<{ page: number; gamesBeforeTarget: number; targetDate: string; sortOrder: string }>>> =>
        api.get('/games/release-date-page', { params }),

    getRelatedGames: (gameId: string, platformId?: string, limit?: number, country?: string): Promise<AxiosResponse<ApiResponse<FeaturedGamePlatform[]>>> =>
        api.get(`/games/${gameId}/related`, { params: { platformId, limit: limit || 10, country } }),

    getMostPopularForPlatform: (platformSlug: string, country?: string): Promise<AxiosResponse<ApiResponse<FeaturedGamePlatform[]>>> =>
        api.get(`/games/platform/${platformSlug}/popular`, { params: { country } }),

    getBiggestPriceDropsForPlatform: (platformSlug: string, country?: string): Promise<AxiosResponse<PaginatedApiResponse<PriceDropGamePlatform>>> =>
        api.get('/games/biggest-price-drops', { params: { page: 1, limit: 15, platform: platformSlug, country } }),

    getRecentGamesForPlatform: (platformSlug: string, country?: string): Promise<AxiosResponse<ApiResponse<FeaturedGamePlatform[]>>> =>
        api.get(`/games/platform/${platformSlug}/recent`, { params: { country } }),

    getAdminGamePlatforms: (params?: {
        page?: number;
        limit?: number;
        search?: string;
        platformId?: string;
        platformSlug?: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        period?: string;
    }): Promise<AxiosResponse<PaginatedApiResponse<GamePlatform & { viewCount: number; storeClickCount: number; lowestPrice?: { amount: number; currency: string; store?: { id: string; name: string } | null } | null }>>> =>
        api.get('/games/admin/game-platforms', { params }),

    getAdminGamePlatformsWithMultipleEans: (params?: {
        page?: number;
        limit?: number;
        search?: string;
        platformId?: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
    }): Promise<AxiosResponse<PaginatedApiResponse<GamePlatform & { eanCount: number; eanValues: string[] }>>> =>
        api.get('/games/admin/game-platforms-with-multiple-eans', { params }),

    invalidateSearchCache: (): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.post('/games/invalidate-search-cache'),

    // Game Platform management
    createGamePlatform: (data: { gameId: string; platformId: string; imageUrl?: string; releaseDate?: string; metacriticScore?: number }): Promise<AxiosResponse<ApiResponse<GamePlatform>>> =>
        api.post('/games/game-platforms', data),

    updateGamePlatform: (gameId: string, platformId: string, data: { imageUrl?: string; releaseDate?: string; metacriticScore?: number }): Promise<AxiosResponse<ApiResponse<GamePlatform>>> =>
        api.put(`/games/game-platforms/${gameId}/${platformId}`, data),

    deleteGamePlatform: (gameId: string, platformId: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/games/game-platforms/${gameId}/${platformId}`),

    // Price management
    movePrice: (data: { priceId: string; newGameId: string; newPlatformId: string }): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.post('/games/move-price', data),

    updateLowestPrice: (data: { gameId: string; platformId: string }): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.post('/games/update-lowest-price', data),

    // Game merging
    mergeGames: (data: { gameIds: string[]; targetGameId: string; newTitle: string }): Promise<AxiosResponse<ApiResponse<{ mergedGame: Game; deletedGames: string[]; totalGamesMerged: number }>>> =>
        api.post('/games/merge', data),
}

// Platforms API
export const platformsApi = {
    getPlatforms: (page?: number, limit?: number, search?: string): Promise<AxiosResponse<PaginatedApiResponse<Platform>>> =>
        api.get('/platforms', { params: { page, limit, search } }),

    getPlatform: (slug: string): Promise<AxiosResponse<ApiResponse<Platform>>> =>
        api.get(`/platforms/slug/${slug}`),

    getPlatformById: (id: string): Promise<AxiosResponse<ApiResponse<Platform>>> =>
        api.get(`/platforms/${id}`),

    createPlatform: (data: Partial<Platform>): Promise<AxiosResponse<ApiResponse<Platform>>> =>
        api.post('/platforms', data),

    updatePlatform: (id: string, data: Partial<Platform>): Promise<AxiosResponse<ApiResponse<Platform>>> =>
        api.put(`/platforms/${id}`, data),

    deletePlatform: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/platforms/${id}`),
}

// Platform Aliases API
export const platformAliasesApi = {
    getPlatformAliases: (platformId?: string): Promise<AxiosResponse<ApiResponse<PlatformAlias[]>>> =>
        api.get('/platform-aliases', { params: { platformId } }),

    getPlatformAlias: (id: string): Promise<AxiosResponse<ApiResponse<PlatformAlias>>> =>
        api.get(`/platform-aliases/${id}`),

    createPlatformAlias: (data: { platformId: string; name: string }): Promise<AxiosResponse<ApiResponse<PlatformAlias>>> =>
        api.post('/platform-aliases', data),

    updatePlatformAlias: (id: string, data: { name: string }): Promise<AxiosResponse<ApiResponse<PlatformAlias>>> =>
        api.put(`/platform-aliases/${id}`, data),

    deletePlatformAlias: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/platform-aliases/${id}`),
}

// Genres API
export const genresApi = {
    getGenres: (): Promise<AxiosResponse<PaginatedApiResponse<Genre[]>>> =>
        api.get('/genres'),

    createGenre: (data: Partial<Genre>): Promise<AxiosResponse<ApiResponse<Genre>>> =>
        api.post('/genres', data),

    updateGenre: (id: string, data: Partial<Genre>): Promise<AxiosResponse<ApiResponse<Genre>>> =>
        api.put(`/genres/${id}`, data),

    deleteGenre: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/genres/${id}`),
}

// Stores API
export const storesApi = {
    getStores: (params?: { page?: number; limit?: number; search?: string; country?: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }): Promise<AxiosResponse<PaginatedApiResponse<Store & { _count: { prices: number; ratings: number } }>>> =>
        api.get('/stores', { params }),

    getStore: (id: string): Promise<AxiosResponse<ApiResponse<Store & { prices: Price[], _count: { prices: number } }>>> =>
        api.get(`/stores/${id}`),

    createStore: (data: Partial<Store>): Promise<AxiosResponse<ApiResponse<Store>>> =>
        api.post('/stores', data),

    updateStore: (id: string, data: Partial<Store>): Promise<AxiosResponse<ApiResponse<Store>>> =>
        api.put(`/stores/${id}`, data),

    deleteStore: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/stores/${id}`),
}

// Prices API
export const pricesApi = {
    getPrices: (params?: { gameId?: string; platformId?: string; storeId?: string; page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }): Promise<AxiosResponse<PaginatedApiResponse<Price>>> =>
        api.get('/prices', { params }),

    getPriceHistory: (gameId: string, platformId: string, storeId?: string, days?: number): Promise<AxiosResponse<ApiResponse<Price[]>>> =>
        api.get(`/prices/history/${gameId}/${platformId}`, { params: { ...(storeId && { storeId }), ...(days && { days }) } }),

    getLowestPrice: (gameId: string, platformId: string, country?: string): Promise<AxiosResponse<ApiResponse<LowestPrice>>> =>
        api.get(`/prices/lowest/${gameId}/${platformId}`, { params: { country } }),

    getAllLowestPrices: (gameId: string, platformId: string): Promise<AxiosResponse<ApiResponse<LowestPrice[]>>> =>
        api.get(`/prices/lowest/${gameId}/${platformId}/all`),

    createPrice: (data: { gameId: string; platformId: string; storeId: string; amount: number; currency: string; storeUrl?: string; shipping?: number; total?: number }): Promise<AxiosResponse<ApiResponse<Price>>> =>
        api.post('/prices', data),

    updatePrice: (id: string, data: Partial<Price>): Promise<AxiosResponse<ApiResponse<Price>>> =>
        api.put(`/prices/${id}`, data),

    deletePrice: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/prices/${id}`),

    deleteAllPricesForStore: (storeId: string): Promise<AxiosResponse<ApiResponse<DeleteAllPricesResponse>>> =>
        api.delete(`/prices/store/${storeId}`),
}

// Wishlist API
export const wishlistApi = {
    getWishlist: (country?: string): Promise<AxiosResponse<ApiResponse<WishlistItem[]>>> =>
        api.get('/wishlists', { params: { country } }),

    addToWishlist: (gameId: string, platformId: string, maxPrice: number, isOwned: boolean = false, country?: string): Promise<AxiosResponse<ApiResponse<WishlistItem>>> =>
        api.post('/wishlists', { gameId, platformId, maxPrice, isOwned, country }, { params: { country } }),

    updateWishlist: (id: string, data: { maxPrice?: number; isOwned?: boolean }, country?: string): Promise<AxiosResponse<ApiResponse<WishlistItem>>> =>
        api.put(`/wishlists/${id}`, data, { params: { country } }),

    removeFromWishlist: (id: string, country?: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/wishlists/${id}`, { params: { country } }),

    checkWishlist: (gameId: string, platformId?: string, country?: string): Promise<AxiosResponse<ApiResponse<WishlistItem | WishlistItem[] | null>>> =>
        platformId
            ? api.get(`/wishlists/check/${gameId}/${platformId}`, { params: { country } })
            : api.get(`/wishlists/check/${gameId}`, { params: { country } }),

    checkWishlistAllCountries: (gameId: string, platformId?: string): Promise<AxiosResponse<ApiResponse<WishlistItem[]>>> =>
        platformId
            ? api.get(`/wishlists/check/${gameId}/${platformId}/all-countries`)
            : api.get(`/wishlists/check/${gameId}/all-countries`),
}

// Notifications API
export const notificationsApi = {
    getNotifications: (params?: { page?: number; limit?: number; isRead?: boolean }): Promise<AxiosResponse<PaginatedApiResponse<Notification>>> =>
        api.get('/notifications', { params }),

    getUnreadCount: (): Promise<AxiosResponse<ApiResponse<{ count: number }>>> =>
        api.get('/notifications/unread-count'),

    markAsRead: (id: string): Promise<AxiosResponse<ApiResponse<Notification>>> =>
        api.put(`/notifications/${id}/read`),

    markAllAsRead: (): Promise<AxiosResponse<ApiResponse<{ updated: number }>>> =>
        api.put('/notifications/mark-all-read'),

    deleteNotification: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/notifications/${id}`),

    deleteAllNotifications: (): Promise<AxiosResponse<ApiResponse<{ deleted: number }>>> =>
        api.delete('/notifications'),
}

// Product IDs API
export const productIdsApi = {
    getGamePlatformProductIds: (gameId: string, platformId?: string): Promise<AxiosResponse<ApiResponse<GamePlatformProductId[]>>> =>
        api.get('/game-platform-product-ids', { params: { gameId, platformId } }),

    getGameExternalIds: (gameId: string): Promise<AxiosResponse<ApiResponse<GameExternalId[]>>> =>
        api.get('/game-external-ids', { params: { gameId } }),

    createProductId: (data: { gameId: string; platformId: string; type: 'EAN' | 'UPC'; value: string }): Promise<AxiosResponse<ApiResponse<GamePlatformProductId>>> =>
        api.post('/game-platform-product-ids', data),

    updateProductId: (id: string, data: Partial<{ type: 'EAN' | 'UPC'; value: string }>): Promise<AxiosResponse<ApiResponse<GamePlatformProductId>>> =>
        api.put(`/game-platform-product-ids/${id}`, data),

    deleteProductId: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/game-platform-product-ids/${id}`),

    createExternalId: (data: { gameId: string; externalId: string; sourceId: string; url?: string }): Promise<AxiosResponse<ApiResponse<GameExternalId>>> =>
        api.post('/game-external-ids', data),

    updateExternalId: (id: string, data: Partial<{ externalId: string; sourceId: string; url?: string }>): Promise<AxiosResponse<ApiResponse<GameExternalId>>> =>
        api.put(`/game-external-ids/${id}`, data),

    deleteExternalId: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/game-external-ids/${id}`),

    // EAN management
    moveEan: (data: { eanId: string; newGameId: string; newPlatformId: string }): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.post('/game-platform-product-ids/move-ean', data),
}

// Analytics API
export const analyticsApi = {
    trackPageView: (data: {
        gameId: string;
        platformId?: string;
        referrer?: string;
        sessionId?: string;
    }): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.post('/analytics/page-views', data),

    trackStoreClick: (data: {
        gameId: string;
        platformId?: string;
        storeId: string;
        clickType: 'BEST_PRICE' | 'INDIVIDUAL_PRICE';
        priceAmount?: number;
        currency?: string;
        referrer?: string;
        sessionId?: string;
    }): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.post('/analytics/store-clicks', data),

    getPopularGames: (params?: {
        startDate?: string;
        endDate?: string;
        limit?: string;
        page?: string;
        search?: string;
        platformId?: string;
    }): Promise<AxiosResponse<ApiResponse<Array<{ game: Game; viewCount: number; totalViews: number; uniqueViews: number }>>>> =>
        api.get('/analytics/popular-games', { params }),

    getPopularGamePlatforms: (params?: {
        startDate?: string;
        endDate?: string;
        limit?: string;
        page?: string;
        search?: string;
        platformId?: string;
    }): Promise<AxiosResponse<ApiResponse<Array<{ gamePlatform: GamePlatform; viewCount: number }>>>> =>
        api.get('/analytics/popular-game-platforms', { params }),

    getGameStats: (gameId: string, params?: { days?: string }): Promise<AxiosResponse<ApiResponse<{ gameId: string; viewStats: object; chartData: Array<object>; period: number }>>> =>
        api.get(`/analytics/game-stats/${gameId}`, { params }),

    getOverview: (): Promise<AxiosResponse<ApiResponse<{
        todayViews: number;
        totalViews: number;
        uniqueViewsToday: number;
    }>>> =>
        api.get('/analytics/overview'),

    getStoreClickStats: (params?: {
        startDate?: string;
        endDate?: string;
        limit?: string;
        page?: string;
        storeId?: string;
    }): Promise<AxiosResponse<ApiResponse<Array<{ store: Store; bestPriceClicks: number; individualPriceClicks: number; totalClicks: number }>>>> =>
        api.get('/analytics/store-clicks', { params }),

    getIndividualStoreClicks: (params?: {
        startDate?: string;
        endDate?: string;
        limit?: string;
        page?: string;
        search?: string;
        platformId?: string;
        storeId?: string;
    }): Promise<AxiosResponse<ApiResponse<Array<{
        id: string;
        gameId: string;
        platformId?: string;
        storeId: string;
        clickType: 'BEST_PRICE' | 'INDIVIDUAL_PRICE';
        priceAmount?: number;
        currency?: string;
        createdAt: string;
        game: { id: string; title: string; slug: string; developer?: string };
        platform?: { id: string; name: string; slug: string };
        store: { id: string; name: string; logo?: string; country?: string; url?: string };
    }>>>> =>
        api.get('/analytics/individual-store-clicks', { params }),

    getAnalyticsPlatforms: (): Promise<AxiosResponse<ApiResponse<Array<{ id: string; name: string; slug: string }>>>> =>
        api.get('/analytics/platforms'),

    getAnalyticsStores: (): Promise<AxiosResponse<ApiResponse<Array<{ id: string; name: string; logo?: string; country?: string; url?: string }>>>> =>
        api.get('/analytics/stores'),

    getGameClickStats: (gameId: string, params?: { days?: string }): Promise<AxiosResponse<ApiResponse<{ totalClicks: number; uniqueStores: number; bestPriceClicks: number; individualPriceClicks: number; storeBreakdown: Array<object> }>>> =>
        api.get(`/analytics/game-clicks/${gameId}`, { params }),
}

// Users API
export const usersApi = {
    getUsers: (params?: { page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: 'asc' | 'desc'; isAdmin?: boolean; country?: string }): Promise<AxiosResponse<PaginatedApiResponse<User & { isEmailVerified: boolean; _count: { wishlist: number; notifications: number } }>>> =>
        api.get('/users', { params }),

    getUser: (id: string): Promise<AxiosResponse<ApiResponse<User & {
        isEmailVerified: boolean;
        wishlist: WishlistItem[];
        notifications: Notification[];
        _count: { wishlist: number; notifications: number };
    }>>> =>
        api.get(`/users/${id}`),

    createUser: (data: { email: string; password: string; country: string; isAdmin?: boolean }): Promise<AxiosResponse<ApiResponse<User>>> =>
        api.post('/users', data),

    updateUser: (id: string, data: Partial<{ email: string; country: string; isAdmin: boolean; isEmailVerified: boolean }>): Promise<AxiosResponse<ApiResponse<User>>> =>
        api.put(`/users/${id}`, data),

    deleteUser: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/users/${id}`),

    verifyUserEmail: (id: string): Promise<AxiosResponse<ApiResponse<User>>> =>
        api.post(`/users/${id}/verify-email`),
}

// Comments API
export const commentsApi = {
    getComments: (gameSlug: string, platformSlug: string, params?: { page?: number | string; limit?: number | string; country?: string }): Promise<AxiosResponse<PaginatedApiResponse<Comment>>> =>
        api.get(`/comments/${gameSlug}/${platformSlug}`, { params }),

    getAdminComments: (params?: { page?: number | string; limit?: number | string; search?: string; isSpam?: boolean }): Promise<AxiosResponse<PaginatedApiResponse<Comment>>> =>
        api.get('/comments/admin', { params }),

    createComment: (gameSlug: string, platformSlug: string, data: CreateCommentData): Promise<AxiosResponse<ApiResponse<Comment>>> =>
        api.post(`/comments/${gameSlug}/${platformSlug}`, data),

    updateComment: (id: string, data: UpdateCommentData): Promise<AxiosResponse<ApiResponse<Comment>>> =>
        api.put(`/comments/${id}`, data),

    deleteComment: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/comments/${id}`),

    markAsSpam: (id: string): Promise<AxiosResponse<ApiResponse<Comment>>> =>
        api.post(`/comments/${id}/mark-spam`),

    unmarkAsSpam: (id: string): Promise<AxiosResponse<ApiResponse<Comment>>> =>
        api.post(`/comments/${id}/unmark-spam`),
}

// Censored Words API (Admin only)
export const censoredWordsApi = {
    getCensoredWords: (params?: { page?: number; limit?: number; search?: string; isActive?: boolean }): Promise<AxiosResponse<PaginatedApiResponse<CensoredWord>>> =>
        api.get('/censored-words', { params }),

    getCensoredWord: (id: string): Promise<AxiosResponse<ApiResponse<CensoredWord>>> =>
        api.get(`/censored-words/${id}`),

    createCensoredWord: (data: { word: string; isActive?: boolean }): Promise<AxiosResponse<ApiResponse<CensoredWord>>> =>
        api.post('/censored-words', data),

    updateCensoredWord: (id: string, data: Partial<{ word: string; isActive: boolean }>): Promise<AxiosResponse<ApiResponse<CensoredWord>>> =>
        api.put(`/censored-words/${id}`, data),

    deleteCensoredWord: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/censored-words/${id}`),

    bulkCreateCensoredWords: (data: { words: string[] }): Promise<AxiosResponse<ApiResponse<{ created: number; total: number; skipped: number }>>> =>
        api.post('/censored-words/bulk', data),
}

// Comment Settings API (Admin only)
export const commentSettingsApi = {
    getCommentSettings: (): Promise<AxiosResponse<ApiResponse<CommentSetting[]>>> =>
        api.get('/comment-settings'),

    getCommentSetting: (key: string): Promise<AxiosResponse<ApiResponse<CommentSetting>>> =>
        api.get(`/comment-settings/${key}`),

    updateCommentSetting: (key: string, data: { value: string; description?: string }): Promise<AxiosResponse<ApiResponse<CommentSetting>>> =>
        api.put(`/comment-settings/${key}`, data),

    initializeCommentSettings: (): Promise<AxiosResponse<ApiResponse<{ created: number; updated: number; total: number }>>> =>
        api.post('/comment-settings/initialize'),
}

// Store Ratings API
export const storeRatingsApi = {
    getStoreRatings: (storeId: string, params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }): Promise<AxiosResponse<ApiResponse<StoreRatingsResponse>>> =>
        api.get(`/stores/${storeId}/ratings`, { params }),

    getMyStoreRating: (storeId: string): Promise<AxiosResponse<ApiResponse<StoreRating | null>>> =>
        api.get(`/stores/${storeId}/ratings/my`),

    createStoreRating: (storeId: string, data: CreateStoreRatingData): Promise<AxiosResponse<ApiResponse<StoreRating>>> =>
        api.post(`/stores/${storeId}/ratings`, data),

    updateStoreRating: (storeId: string, data: UpdateStoreRatingData): Promise<AxiosResponse<ApiResponse<StoreRating>>> =>
        api.put(`/stores/${storeId}/ratings`, data),

    deleteStoreRating: (storeId: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/stores/${storeId}/ratings`),
}

// Game Ratings API
export const gameRatingsApi = {
    getGameRatings: (gameSlug: string, platformSlug: string, params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
        api.get(`/games/${gameSlug}/platforms/${platformSlug}/ratings`, { params }),

    getMyGameRating: (gameSlug: string, platformSlug: string) =>
        api.get(`/games/${gameSlug}/platforms/${platformSlug}/ratings/my`),

    createGameRating: (gameSlug: string, platformSlug: string, data: CreateGameRatingData) =>
        api.post(`/games/${gameSlug}/platforms/${platformSlug}/ratings`, data),

    updateGameRating: (gameSlug: string, platformSlug: string, data: UpdateGameRatingData) =>
        api.put(`/games/${gameSlug}/platforms/${platformSlug}/ratings`, data),

    deleteGameRating: (gameSlug: string, platformSlug: string) =>
        api.delete(`/games/${gameSlug}/platforms/${platformSlug}/ratings`),
};

// FAQ API
export const faqsApi = {
    getFAQs: (): Promise<AxiosResponse<ApiResponse<Array<{
        id: string;
        question: string;
        answer: string;
        order: number;
    }>>>> =>
        api.get('/faqs'),

    getAdminFAQs: (): Promise<AxiosResponse<ApiResponse<Array<{
        id: string;
        question: string;
        answer: string;
        order: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
    }>>>> =>
        api.get('/faqs/admin'),

    createFAQ: (data: { question: string; answer: string; order?: number; isActive?: boolean }): Promise<AxiosResponse<ApiResponse<{
        id: string;
        question: string;
        answer: string;
        order: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
    }>>> =>
        api.post('/faqs', data),

    updateFAQ: (id: string, data: Partial<{ question: string; answer: string; order: number; isActive: boolean }>): Promise<AxiosResponse<ApiResponse<{
        id: string;
        question: string;
        answer: string;
        order: number;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
    }>>> =>
        api.put(`/faqs/${id}`, data),

    deleteFAQ: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/faqs/${id}`),

    reorderFAQs: (data: { faqIds: string[] }): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
        api.post('/faqs/reorder', data),
}

// Contact API
export const contactApi = {
    sendContactMessage: (data: { name: string; email: string; subject: string; message: string; captchaToken: string }): Promise<AxiosResponse<ApiResponse<null>>> =>
        api.post('/contact', data),
}

// Game Platform Images API
export const gamePlatformImagesApi = {
    getAllGamePlatformImages: (params?: {
        page?: number;
        limit?: number;
        search?: string;
        gameId?: string;
        platformId?: string;
        storeId?: string;
        type?: string;
        hasFilename?: boolean;
        isCdnHosted?: boolean;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
    }): Promise<AxiosResponse<PaginatedApiResponse<GamePlatformImage & {
        store: { id: string; name: string; logo?: string };
        gamePlatform?: {
            game: { id: string; title: string; slug: string };
            platform: { id: string; name: string; slug: string };
        };
    }>>> =>
        api.get('/game-platform-images', { params }),

    cacheImage: (id: string): Promise<AxiosResponse<ApiResponse<{
        id: string;
        originalUrl: string;
        cachedUrl?: string;
        localPath?: string;
        wasAlreadyCached: boolean;
    }>>> =>
        api.post(`/game-platform-images/${id}/cache`),

    getGamePlatformImages: (gameId: string, platformId: string, params?: { type?: string; storeId?: string }): Promise<AxiosResponse<ApiResponse<GamePlatformImage[]>>> =>
        api.get(`/game-platform-images/game/${gameId}/platform/${platformId}`, { params }),

    getGamePlatformImage: (id: string): Promise<AxiosResponse<ApiResponse<GamePlatformImage>>> =>
        api.get(`/game-platform-images/${id}`),

    createGamePlatformImage: (data: { gameId: string; platformId: string; storeId: string; type: 'COVER' | 'SCREENSHOT' | 'BANNER' | 'THUMBNAIL' | 'ARTWORK'; url: string; order?: number }): Promise<AxiosResponse<ApiResponse<GamePlatformImage>>> =>
        api.post('/game-platform-images', data),

    updateGamePlatformImage: (id: string, data: Partial<{ type: 'COVER' | 'SCREENSHOT' | 'BANNER' | 'THUMBNAIL' | 'ARTWORK'; url: string; order: number }>): Promise<AxiosResponse<ApiResponse<GamePlatformImage>>> =>
        api.put(`/game-platform-images/${id}`, data),

    deleteGamePlatformImage: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/game-platform-images/${id}`),

    getStoreImages: (storeId: string, params?: { type?: string; gameId?: string; platformId?: string }): Promise<AxiosResponse<ApiResponse<GamePlatformImage[]>>> =>
        api.get(`/game-platform-images/store/${storeId}`, { params }),

    bulkImportImages: (data: { images: Array<{ gameId: string; platformId: string; storeId: string; type: 'COVER' | 'SCREENSHOT' | 'BANNER' | 'THUMBNAIL' | 'ARTWORK'; url: string; order?: number }> }): Promise<AxiosResponse<ApiResponse<{ created: number; skipped: number; errors: string[] }>>> =>
        api.post('/game-platform-images/bulk-import', data),
}

// Sales API
export const salesApi = {
    getSales: (params?: { storeId?: string; startDate?: string; endDate?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }): Promise<AxiosResponse<PaginatedApiResponse<Sale>>> =>
        api.get('/sales', { params }),

    createSale: (data: Partial<Sale>): Promise<AxiosResponse<ApiResponse<Sale>>> =>
        api.post('/sales', data),

    updateSale: (id: string, data: Partial<Sale>): Promise<AxiosResponse<ApiResponse<Sale>>> =>
        api.put(`/sales/${id}`, data),

    deleteSale: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
        api.delete(`/sales/${id}`),
}

export default api